import {
  Dropdown,
  Link,
  activitesFromEntreprise,
  isIas,
  isIobsp,
} from "@endya-conformite/ui-kit";
import RedirectIcon from "@endya-conformite/ui-kit/src/assets/icons/redirect.svg?react";
import ThreeDots from "@endya-conformite/ui-kit/src/assets/icons/three-dots-vertical.svg?react";
import { AvancementSociete } from "@gateway/conformite/avancement/controller/avancement.definition";
import { Categorie } from "@gateway/info-societe-orias/service/info-societe-orias.definition";
import style from "./EntrepriseActionsMenu.module.scss";

type EntrepriseActionsMenuProps = {
  idEntreprise: string;
  siren: string;
  idAxe: string;
  idControleQuinquennal: string;
  idContactPrincipal?: string;
  idEntreprisePrincipal?: string;
  categoriesActivites?: Categorie[];
  categoriesActivitesAccessoires?: Categorie[];
  avancement?: AvancementSociete;
};

type MenuItemConfig = {
  label: string;
  url: string;
  show?: boolean;
};

function MenuItem({ label, url }: MenuItemConfig) {
  return (
    <Link target="_blank" to={url} className={style.menuItem}>
      <>
        {label}
        <RedirectIcon />
      </>
    </Link>
  );
}

export function EntrepriseActionsMenu({
  idEntreprise,
  siren,
  idAxe,
  idControleQuinquennal,
  idContactPrincipal,
  idEntreprisePrincipal,
  categoriesActivites = [],
  categoriesActivitesAccessoires = [],
  avancement,
}: EntrepriseActionsMenuProps) {
  const frontAdherentUrl = import.meta.env.VITE_FRONT_ADHERENT_URL;
  const baseAdminUrl = `${frontAdherentUrl}/admin/controle-quinquennal/${idEntreprise}`;

  const activites = activitesFromEntreprise({
    categoriesActivites,
    categoriesActivitesAccessoires,
  });
  const hasIAS = isIas(activites);
  const hasIOBSP = isIobsp(activites);

  const hasDeclarationIas = avancement?.declarationActiviteIas != null;
  const hasDeclarationIobsp = avancement?.declarationActiviteIobsp != null;

  const canTakeover = !!idContactPrincipal && !!idEntreprisePrincipal;
  const buildTakeoverUrl = (redirectTo: string) =>
    `${frontAdherentUrl}/admin/take-over/${idContactPrincipal}/${idEntreprisePrincipal}?redirectTo=${encodeURIComponent(
      redirectTo
    )}`;

  const adminDeclarationIasUrl = hasDeclarationIas
    ? `${baseAdminUrl}/declaration-activite/ias`
    : baseAdminUrl;
  const declarationIasUrl = canTakeover
    ? buildTakeoverUrl(`/conformite/declaration-activite/ias/${siren}`)
    : adminDeclarationIasUrl;

  const adminDeclarationIobspUrl = hasDeclarationIobsp
    ? `${baseAdminUrl}/declaration-activite/iobsp`
    : baseAdminUrl;
  const declarationIobspUrl = canTakeover
    ? buildTakeoverUrl(`/conformite/declaration-activite/iobsp/${siren}`)
    : adminDeclarationIobspUrl;

  const derniereSaisieUrl = canTakeover
    ? buildTakeoverUrl(
        `/controle-quinquennal/${idControleQuinquennal}/${idAxe}`
      )
    : "";

  const menuItems: MenuItemConfig[] = [
    {
      label: "Déclaration d'activité IAS",
      url: declarationIasUrl,
      show: hasIAS,
    },
    {
      label: "Déclaration d'activité IOBSP",
      url: declarationIobspUrl,
      show: hasIOBSP,
    },
    {
      label: "Dernière saisie de l'adhérent",
      url: derniereSaisieUrl,
      show: canTakeover,
    },
  ];

  return (
    <Dropdown
      icon={<ThreeDots width={24} height={24} className={style.menuButton} />}
      variant="text"
      className={style.menuTrigger}
      dismissOnClick
    >
      {() => (
        <div className={style.menuContent}>
          {menuItems
            .filter((item) => item.show)
            .map((item) => (
              <MenuItem key={item.label} label={item.label} url={item.url} />
            ))}
        </div>
      )}
    </Dropdown>
  );
}
